import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import { Alert, AlertTitle, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useAuth } from '@/shared/auth/useAuth';
import { env } from '@/shared/config/env';

function BrandMark() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 56,
        height: 56,
        borderRadius: 3,
        display: 'grid',
        placeItems: 'center',
        color: '#fff',
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
        boxShadow: '0 8px 24px rgba(18, 124, 113, 0.32)',
      }}
    >
      <StorefrontRoundedIcon sx={{ fontSize: 30 }} />
    </Box>
  );
}

/**
 * Reads the one-shot `?error=` code the BFF appends when an OAuth2 login could not be
 * completed, then strips it from the address bar so a refresh does not resurrect a stale
 * message. Returns the code, or null when the visitor simply navigated here.
 *
 * Before this existed the BFF had no failure handler at all, so a failed login left the user
 * on the BFF's own origin staring at a raw JSON 404 — see `OAuth2LoginFailureHandler` in
 * web-merchant-portal-bff. The BFF now redirects here instead; this is the other half of that
 * contract.
 */
function useLoginError(): string | null {
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (!error) {
      return;
    }
    setCode(error);
    params.delete('error');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
  }, []);

  return code;
}

/** Public landing / sign-in page shown to unauthenticated visitors. */
export function LandingPage() {
  const { t } = useTranslation('app');
  const { login } = useAuth();
  const loginError = useLoginError();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <BrandMark />
            <Box>
              <Typography variant="h4" component="h1">
                {env.appName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('landing.tagline')}
              </Typography>
            </Box>

            {loginError && (
              <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                <AlertTitle>{t('landing.error.title')}</AlertTitle>
                {/* Falls back to the generic message for any code we have no specific copy
                    for — the code set is Keycloak's, not a closed list we control. */}
                {t(`landing.error.${loginError}`, { defaultValue: t('landing.error.generic') })}
              </Alert>
            )}

            <Button
              fullWidth
              size="large"
              variant="contained"
              startIcon={<LoginRoundedIcon />}
              onClick={login}
            >
              {loginError ? t('landing.error.retry') : t('landing.signIn')}
            </Button>

            <Typography variant="body2" color="text.secondary">
              {t('landing.newToPlatform')}{' '}
              <Box component={RouterLink} to="/sign-up" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {t('landing.createAccount')}
              </Box>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
